import { UserTypes } from '../constants';

/**
 * The navigation model, shared by the drawer and the content pills.
 *
 * The design has no persistent rail — `showRail` is hardcoded false in its
 * view model and the shell is a single column. Every nav surface here is
 * either the overlay drawer or the pill row above a page title.
 */

/**
 * Primary views, in the design's order.
 *
 * `ready: false` marks a view whose screen has not been built yet. Those are
 * rendered disabled rather than hidden, so the shape of the product is visible
 * and the entry lights up when its phase lands — flip the flag, nothing else.
 */
export const VIEWS = [
  { key: 'overview', label: 'Overview', path: '/dashboard/overview', ready: true },
  { key: 'map', label: 'Live map', path: '/dashboard/map', ready: true },
  { key: 'devices', label: 'Devices', path: '/dashboard/devices', ready: true },
  { key: 'people', label: 'Staff', path: '/dashboard/people', ready: true },
  { key: 'alerts', label: 'Alerts', path: '/dashboard/alerts', ready: true }
];

/**
 * Configuration entries. Super and IT only; `superOnly` narrows an entry to
 * Super. Network performance lives here rather than among the views because
 * only Super users can open it.
 *
 * `setupOnly` marks the tables that only change in setup mode. The drawer
 * shows them with a padlock outside it, as the design does. They still open:
 * outside setup mode they are read-only.
 */
export const CONFIG_NAV = [
  { key: 'config', label: 'Platform settings', path: '/admin' },
  { key: 'access', label: 'Access log', path: '/admin/access', superOnly: true },
  { key: 'network', label: 'Network performance', path: '/network', superOnly: true },
  { key: 'users', label: 'SimulAlert users', path: '/admin/users', setupOnly: true },
  { key: 'staff', label: 'Staff', path: '/admin/staff', setupOnly: true },
  { key: 'rooms', label: 'Rooms', path: '/admin/rooms', setupOnly: true },
  { key: 'floors', label: 'Building floors', path: '/admin/floors', setupOnly: true },
  { key: 'buildings', label: 'Buildings', path: '/admin/buildings', setupOnly: true },
  { key: 'hubs', label: 'Hubs', path: '/admin/hubs', setupOnly: true },
  { key: 'badges', label: 'Badges', path: '/admin/badges', setupOnly: true },
  { key: 'texts', label: 'Red alert texts', path: '/admin/red-alert-text-groups', setupOnly: true }
];

/** Paths where the design hides the content pills: configuration and account. */
const NO_PILLS = ['/admin', '/network', '/account', '/auth'];

/**
 * @param {object} userType a member of UserTypes
 * @return {boolean} whether this user may see the Configuration section
 */
export const canConfigure = (userType) =>
  userType === UserTypes.SUPER || userType === UserTypes.IT;

/**
 * @param {object} userType a member of UserTypes
 * @return {Array} the configuration entries this user can reach
 */
export const configNavFor = (userType) =>
  CONFIG_NAV.filter((item) => !item.superOnly || userType === UserTypes.SUPER);

/**
 * @param {object} userType a member of UserTypes
 * @return {Array} the views this user can reach
 */
export const viewsFor = (userType) =>
  VIEWS.filter((view) => !view.superOnly || userType === UserTypes.SUPER);

/**
 * Whether the pill row belongs on this screen. The design hides it on
 * configuration, account and the district rollup.
 *
 * @param {string} pathname
 * @return {boolean}
 */
export const showPills = (pathname) =>
  !NO_PILLS.some((prefix) => pathname === prefix || pathname.startsWith(prefix + '/'));

/**
 * Whether a nav entry is the one currently showing.
 *
 * Matches on a path prefix because the router uses non-exact v5 routes: on
 * /admin/rooms/4 the "Rooms" entry should still read as active. '/admin' is
 * special-cased — as a prefix it matches every other config path, so it only
 * counts when it is the whole path.
 *
 * @param {string} pathname
 * @param {string} path
 * @return {boolean}
 */
export const isActive = (pathname, path) => {
  if (path === '/admin') return pathname === '/admin' || pathname === '/admin/';
  return pathname === path || pathname.startsWith(path + '/');
};
